import * as React from "react";
import { Link } from "react-router-dom";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import useMcpServers from "@/hooks/useMcpServers";
import useSapConfigs from "@/hooks/useSapConfigs";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function SapAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { items: sapConfigs, loading: sapLoading } = useSapConfigs();
  const { items: mcpServers } = useMcpServers();

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));
  const sapOptions = sapConfigs.map((c) => ({
    value: c.id,
    label: c.companyDb ? `${c.sapUrl} · ${c.companyDb}` : c.sapUrl
  }));

  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 mb-3">
        <p className="text-xs font-semibold text-blue-800 mb-1">How this node works</p>
        <p className="text-xs text-blue-700 leading-relaxed">
          This node gives your AI direct access to SAP — business partners, orders, invoices,
          inventory, and service calls. No MCP server needed; skills are built in.
        </p>
      </div>

      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found.{" "}
            <Link to="/bots" className="underline font-medium">Create one first →</Link>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
      </FormGroup>

      <FormGroup label="SAP Config">
        {!sapLoading && sapConfigs.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No SAP integration found.{" "}
            <Link to="/integrations/sap" className="underline font-medium">Connect SAP first →</Link>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select SAP Config —" }, ...sapOptions]}
            value={node.sapConfigId ?? 0}
            onChange={(value) => onUpdate({ sapConfigId: Number(value) || undefined })}
          />
        )}
      </FormGroup>

      <div className="mt-4 pt-3 border-t border-slate-200">
        <AISpecialistSection
          node={node}
          onUpdate={onUpdate}
          aiConfigurations={aiConfigurations}
          enabledMcpServers={enabledMcpServers}
        />
      </div>

      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>

      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
